'use client';

import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

import {
  clearPreviewLiveValues,
  setPreviewLiveValue,
} from '~store/PreviewLive';

import { PreviewBridgeProps } from './PreviewBridge.types';

const STEGA_GLOBAL_KEY = '__STRAPI_STEGA__';
const PREVIEW_SCRIPT_ID = 'strapi-preview-script';
const STRAPI_SCRIPT = 'strapiScript';
const STRAPI_UPDATE = 'strapiUpdate';
const STRAPI_FIELD_CHANGE = 'strapiFieldChange';
const PREVIEW_READY = 'previewReady';
const PREVIEW_RELOADING = 'strapiPreviewReloading';
const PREVIEW_NOTIFICATION = 'strapiPreviewNotification';
const AUTOSAVE_DELAY_MS = 300;
const REFRESH_SETTLE_MS = 500;
const STRUCTURAL_CHANGE_DEBOUNCE_MS = 100;

const NOTIFY_SAVE_TEXT = 'Сохраните изменения для обновления превью';

const sendNotification = (cmsOrigin: string, message: string) => {
  window.parent.postMessage({ type: PREVIEW_NOTIFICATION, message }, cmsOrigin);
};

const resolveMarkdownSources = (field: string) => {
  const sources = new Set<string>();
  document.querySelectorAll('[data-strapi-markdown]').forEach((element) => {
    const source = element.getAttribute('data-strapi-source');
    if (source?.includes(`path=${field}`)) {
      sources.add(source);
    }
  });
  return Array.from(sources);
};

const CDN_STEGA_IMPORT = /await\s+import\([^)]*@vercel\/stega[^)]*\)/;
const BUNDLER_LOADER_CALL =
  /await\s+[A-Za-z_$][\w$]*\(\s*(async\s*\(\s*\)\s*=>\s*\{[\s\S]*?\})\s*,\s*\[\s*\]\s*\)/g;
const NON_RENDERED_SELECTOR = 'script, style, template, noscript';

const stripStegaFromNonRenderedElements = (
  stega: typeof import('@vercel/stega'),
) => {
  document.querySelectorAll(NON_RENDERED_SELECTOR).forEach((element) => {
    const { textContent } = element;
    if (!textContent) return;
    const cleaned = stega.vercelStegaClean(textContent);
    if (cleaned !== textContent) element.textContent = cleaned;
  });
};

type StrapiPreviewMessage = {
  type?: string;
  payload?: { script?: string; field?: string; value?: unknown };
};

export function PreviewBridge({ cmsOrigin }: PreviewBridgeProps) {
  const router = useRouter();

  useEffect(() => {
    const stegaPromise = import('@vercel/stega');
    window.parent.postMessage({ type: PREVIEW_READY }, cmsOrigin);
    let isInjectionStarted = false;

    let structuralChangeTimer: ReturnType<typeof setTimeout>;

    const handleMessage = async (event: MessageEvent<StrapiPreviewMessage>) => {
      if (event.origin !== cmsOrigin || event.source !== window.parent) return;

      if (event.data?.type === STRAPI_FIELD_CHANGE) {
        const { field, value } = event.data.payload ?? {};
        if (typeof field !== 'string') return;

        if (value === undefined) {
          clearTimeout(structuralChangeTimer);
          structuralChangeTimer = setTimeout(() => {
            sendNotification(cmsOrigin, NOTIFY_SAVE_TEXT);
          }, STRUCTURAL_CHANGE_DEBOUNCE_MS);
          return;
        }

        if (typeof value === 'string') {
          const sources = resolveMarkdownSources(field);
          if (sources.length > 0) {
            event.stopImmediatePropagation();
            void stegaPromise.then((stega) => {
              const cleaned = stega.vercelStegaClean(value);
              sources.forEach((source) => setPreviewLiveValue(source, cleaned));
            });
          }
        }
        return;
      }

      if (event.data?.type === STRAPI_UPDATE) {
        window.parent.postMessage({ type: PREVIEW_RELOADING }, cmsOrigin);
        clearPreviewLiveValues();
        setTimeout(() => {
          router.refresh();
          setTimeout(() => {
            window.parent.postMessage({ type: PREVIEW_READY }, cmsOrigin);
          }, REFRESH_SETTLE_MS);
        }, AUTOSAVE_DELAY_MS);
        return;
      }

      if (event.data?.type !== STRAPI_SCRIPT) return;

      const script = event.data.payload?.script;
      if (
        !script ||
        isInjectionStarted ||
        document.getElementById(PREVIEW_SCRIPT_ID)
      )
        return;
      isInjectionStarted = true;

      const patched = script
        .replace(CDN_STEGA_IMPORT, `window.${STEGA_GLOBAL_KEY}`)
        .replace(BUNDLER_LOADER_CALL, 'await ($1)()');
      if (patched.includes('cdn.jsdelivr.net')) {
        isInjectionStarted = false;
        console.error(
          '[PreviewBridge] Failed to patch preview script — CDN request detected.',
        );
        return;
      }

      let stega: typeof import('@vercel/stega');
      try {
        stega = await stegaPromise;
      } catch (error) {
        isInjectionStarted = false;
        console.error('[PreviewBridge] Failed to load @vercel/stega', error);
        return;
      }

      stripStegaFromNonRenderedElements(stega);
      (window as unknown as Record<string, unknown>)[STEGA_GLOBAL_KEY] = stega;

      const element = document.createElement('script');
      element.id = PREVIEW_SCRIPT_ID;
      const nonce =
        document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;
      if (nonce) element.nonce = nonce;
      element.textContent = patched;
      document.head.appendChild(element);
    };

    window.addEventListener('message', handleMessage);
    return () => {
      clearTimeout(structuralChangeTimer);
      window.removeEventListener('message', handleMessage);
      const cleanupWindow = window as unknown as Record<string, unknown>;
      (cleanupWindow.__strapi_previewCleanup as (() => void) | undefined)?.();
      delete cleanupWindow.__strapi_previewCleanup;
      document.getElementById(PREVIEW_SCRIPT_ID)?.remove();
    };
  }, [cmsOrigin, router]);

  return null;
}
